import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const stamp = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

const Stat = ({ label, value, tone = "text-[#11211A]", testId }) => (
  <div className="border border-[#E8ECE9] p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">{label}</div>
    <div className={`font-serif text-2xl ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

export const FollowupAuditPanel = ({ onClose }) => {
  const [day, setDay] = useState(localDay());
  const [linkSince, setLinkSince] = useState("2026-09-23");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data: d } = await api.get(`/admin/free-listings/followup-audit?day=${day}&tz_offset=${new Date().getTimezoneOffset()}&link_since=${linkSince}`, { timeout: 90000 });
      setData(d);
    } catch (e) {
      const msg = e.response?.status === 401 ? "Your admin session has expired — please sign in again." : (e.response?.data?.detail || e.message || "Audit failed");
      setError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const c = data?.counts || {};
  return (
    <div className="border-2 border-[#264839] bg-white p-6 mt-4" data-testid="followup-audit-modal">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-serif text-xl text-[#11211A]">7-day follow-up audit</div>
            <p className="text-xs text-[#7D8C85] mt-1">Who received Email 2 on the chosen day, and how old their claim link (Email 1) was at that moment. Read-only.</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="followup-audit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <label className="text-xs text-[#7D8C85]">Email 2 sent on<br /><input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="followup-audit-day" /></label>
          <label className="text-xs text-[#7D8C85]">Count links sent on/after<br /><input type="date" value={linkSince} onChange={(e) => setLinkSince(e.target.value)} className="mt-1 px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm" data-testid="followup-audit-link-since" /></label>
          <button type="button" onClick={load} disabled={busy} className="btn-secondary text-xs disabled:opacity-50" data-testid="followup-audit-run">{busy ? "Checking…" : "Run audit"}</button>
        </div>
        {busy && !data && <p className="text-xs text-[#7D8C85] mt-4" data-testid="followup-audit-loading">Reading the database and the Resend delivery log — this can take up to a minute.</p>}
        {error && <p className="text-sm text-[#B35C4A] bg-red-50 border border-[#B35C4A]/40 p-3 mt-4" data-testid="followup-audit-error">{error}</p>}
        {data && (
          <>
            <div className="grid sm:grid-cols-4 gap-3 mt-5">
              <Stat label={`Email 2 sent ${data.day}`} value={c.sent ?? 0} testId="followup-audit-sent" />
              <Stat label="Link under 7 days at send" value={c.under_7_days_at_send ?? 0} tone={c.under_7_days_at_send ? "text-[#B35C4A]" : "text-[#264839]"} testId="followup-audit-under7" />
              <Stat label={`Link now dated ≥ ${linkSince}`} value={c.link_on_or_after_since ?? 0} testId="followup-audit-since" />
              <Stat label="Link re-sent AFTER Email 2" value={c.link_resent_after_followup ?? 0} tone={c.link_resent_after_followup ? "text-[#B8860B]" : "text-[#11211A]"} testId="followup-audit-resent-after" />
            </div>
            {c.link_resent_after_followup > 0 && (
              <p className="text-xs text-[#7A5A08] bg-[#FFFBEB] border border-[#B8860B]/40 p-3 mt-3" data-testid="followup-audit-explainer">
                {c.link_resent_after_followup} of these suppliers had a fresh claim link sent <strong>after</strong> Email 2 went out (e.g. Safe-mode recovery or a bulk re-send), which overwrote their Email 1 date. Their Email 1 column now shows the newer date even though the follow-up was sent against the older link.
              </p>
            )}
            {data.resend && (
              <div className={`mt-3 border p-3 text-xs ${data.resend.ok ? "border-[#264839]/40 bg-[#F3F7F5] text-[#264839]" : "border-[#B35C4A]/40 bg-red-50 text-[#B35C4A]"}`} data-testid="followup-audit-resend">
                <div className="font-medium">Resend delivery log for {data.day}</div>
                {data.resend.ok ? (
                  <div className="mt-1">
                    Email 2 accepted by Resend: <strong data-testid="followup-audit-resend-count">{data.resend.followups_accepted}</strong>
                    {Object.keys(data.resend.followups_by_status || {}).length > 0 && <> · {Object.entries(data.resend.followups_by_status).map(([s, n]) => `${s} ${n}`).join(" · ")}</>}
                    {" "}· all emails that day: {data.resend.all_emails_that_day}{data.resend.truncated ? " (log truncated)" : ""}
                    {data.resend.stamped_but_not_in_resend?.length > 0 && (
                      <div className="mt-1 text-[#B35C4A]" data-testid="followup-audit-phantom">
                        {data.resend.stamped_but_not_in_resend.length} supplier(s) are stamped as followed-up but Resend has no Email 2 for them — the provider rejected the send (stamps from before this fix could still count a rejected send as “sent”).
                      </div>
                    )}
                  </div>
                ) : <div className="mt-1">Could not read Resend log: {data.resend.error}</div>}
              </div>
            )}
            <div className="mt-4 text-xs text-[#4A5550]"><span className="font-medium">Email 2 recipients by claim-link date (at send):</span> {Object.entries(data.by_link_day || {}).map(([d, n]) => `${d}: ${n}`).join(" · ") || "—"}</div>
            {data.sweeps?.length > 0 && (
              <div className="mt-3 text-xs text-[#4A5550]" data-testid="followup-audit-sweeps">
                <div className="font-medium">Sweep runs (last 7 days):</div>
                <ul className="list-disc pl-5">{data.sweeps.map((s, i) => <li key={i}>{stamp(s.at)} UTC · {s.trigger} · sent {s.stats?.sent ?? 0} / scanned {s.stats?.scanned ?? 0} · too recent {s.stats?.too_recent ?? 0}</li>)}</ul>
              </div>
            )}
            {data.recovery_jobs?.length > 0 && (
              <div className="mt-3 text-xs text-[#4A5550]" data-testid="followup-audit-jobs">
                <div className="font-medium">Safe-mode recovery jobs (last 7 days):</div>
                <ul className="list-disc pl-5">{data.recovery_jobs.map((j) => <li key={j.id}>{stamp(j.created_at)} UTC · {j.status} · claim links re-sent {j.progress?.claims_sent ?? 0}</li>)}</ul>
              </div>
            )}
            <div className="mt-4 max-h-72 overflow-y-auto border border-[#E8ECE9]">
              <table className="w-full text-xs">
                <thead className="bg-[#F7F7F4] text-[#7D8C85] text-left"><tr><th className="p-2">Supplier</th><th className="p-2">Email 1 (now)</th><th className="p-2">Email 1 at send</th><th className="p-2">Age (days)</th><th className="p-2">Email 2</th><th className="p-2">Trigger</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className={`border-t border-[#F0EFEA] ${r.under_7_days ? "bg-red-50" : r.link_resent_after_followup ? "bg-[#FFFBEB]" : ""}`} data-testid={`followup-audit-row-${r.id}`}>
                      <td className="p-2 text-[#11211A]">{r.company} <span className="text-[#7D8C85]">· {r.email}</span></td>
                      <td className="p-2">{stamp(r.claim_link_sent_at)}</td>
                      <td className="p-2">{stamp(r.link_at_send)}</td>
                      <td className="p-2">{r.link_age_days_at_send ?? "?"}</td>
                      <td className="p-2">{stamp(r.claim_followup_sent_at)}</td>
                      <td className="p-2 text-[#7D8C85]">{r.trigger}</td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && <tr><td className="p-3 text-[#7D8C85]" colSpan={6}>No Email 2 sent on this day.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FollowupAuditPanel;
